import { create } from "zustand"

export interface StationTargetRelation {
  station_id: string
  target: {
    id: string
    active: boolean
  } | null
  manual_stop?: boolean
}

const cloneStationTargetRelation = (
  relation: StationTargetRelation,
): StationTargetRelation => ({
  station_id: relation.station_id,
  target: relation.target ? { ...relation.target } : null,
  ...(typeof relation.manual_stop === "boolean"
    ? { manual_stop: relation.manual_stop }
    : {}),
})
export type StationControlMode = "auto" | "manual"

interface StationStore {
  stationTargetRelations: Record<string, StationTargetRelation>
  setStationTargetRelations: (
    relations: Record<string, StationTargetRelation>,
  ) => void
  setStationTargetRelation: (relation: StationTargetRelation) => void
  removeTargetFromAllRelations: (targetId: string) => void
  getStationTargetRelation: (stationId: string) => StationTargetRelation | null
  getStationMode: (stationId: string) => StationControlMode
  getSelectedTargetIdsForStation: (stationId: string) => string[]
}

export const useStationStore = create<StationStore>((set, get) => ({
  stationTargetRelations: {},

  setStationTargetRelations: (relations) =>
    set({
      stationTargetRelations: Object.fromEntries(
        Object.entries(relations).map(([stationId, relation]) => [
          stationId,
          cloneStationTargetRelation(relation),
        ]),
      ),
    }),

  setStationTargetRelation: (relation) =>
    set((state) => ({
      stationTargetRelations: {
        ...state.stationTargetRelations,
        [relation.station_id]: cloneStationTargetRelation(relation),
      },
    })),

  removeTargetFromAllRelations: (targetId) =>
    set((state) => {
      let hasChanges = false
      const nextRelations = Object.fromEntries(
        Object.entries(state.stationTargetRelations).map(
          ([stationId, relation]) => {
            if (relation.target?.id !== targetId) {
              return [stationId, relation]
            }

            hasChanges = true
            return [
              stationId,
              {
                station_id: relation.station_id,
                target: null,
              },
            ]
          },
        ),
      )

      if (!hasChanges) {
        return state
      }

      return {
        stationTargetRelations: nextRelations,
      }
    }),

  getStationTargetRelation: (stationId) => {
    return get().stationTargetRelations[stationId] ?? null
  },

  getStationMode: (stationId) => {
    return get().getStationTargetRelation(stationId)?.target ? "manual" : "auto"
  },

  getSelectedTargetIdsForStation: (stationId) => {
    const targetId = get().getStationTargetRelation(stationId)?.target?.id
    return targetId ? [targetId] : []
  },
}))
